import React, { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Users, AlertTriangle, UserX, Building } from "lucide-react";
import { DutyRosterGroups } from "./DutyRosterGroups";
import { RosterCalendar } from "./RosterCalendar";
import { HolidayOverrideModal } from "./HolidayOverrideModal";
import { useAuth } from "@/contexts/auth";
import { useDutyRosterManagement } from "@/hooks/useDutyRosterManagement";
import { normalizeResidency, RESIDENCY_VALUES } from "@/utils/residencyUtils";

export function DutyRosterManagement() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("groups");
  const [holidayOverrideModalOpen, setHolidayOverrideModalOpen] = useState(false);
  const [selectedGroupForOverride, setSelectedGroupForOverride] = useState<string>("");
  const [selectedMonthForOverride, setSelectedMonthForOverride] = useState<string>("");

  const companyId = user?.companyId || "";
  
  // Get groups data for statistics
  const { groups } = useDutyRosterManagement(companyId);
  
  // Calculate statistics with normalized residency comparison
  const totalGroups = groups.length;
  const residenceGroups = groups.filter(group => 
    normalizeResidency(group.residency_type) === RESIDENCY_VALUES.RESIDENCE
  ).length;
  const nonResidenceGroups = groups.filter(group => 
    normalizeResidency(group.residency_type) === RESIDENCY_VALUES.NON_RESIDENCE
  ).length;

  const handleOpenHolidayOverride = (groupId: string, monthYear: string) => {
    setSelectedGroupForOverride(groupId);
    setSelectedMonthForOverride(monthYear);
    setHolidayOverrideModalOpen(true);
  };

  const handleCloseHolidayOverride = () => {
    setHolidayOverrideModalOpen(false);
    setSelectedGroupForOverride("");
    setSelectedMonthForOverride("");
  };

  if (!companyId) {
    return (
      <div className="container mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-semibold">Company Required</h3>
          <p className="text-red-600 mt-2">Please select a company to manage duty rosters</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Tabs 
        defaultValue="groups" 
        value={activeTab} 
        onValueChange={setActiveTab}
        className="w-full"
      >
        {/* Header with Tabs and Holiday Override Button */}
        <div className="flex items-center justify-between">
          <TabsList className="grid w-full max-w-xs grid-cols-2 bg-background border border-border shadow-sm">
            <TabsTrigger 
              value="groups" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200"
            >
              Groups
            </TabsTrigger>
            <TabsTrigger 
              value="calendar" 
              className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm transition-all duration-200"
            >
              Calendar
            </TabsTrigger>
          </TabsList>
          
          {/* Holiday Override Button - Right Aligned */}
          <Button
            onClick={() => {
              const currentDate = new Date();
              const monthYear = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
              handleOpenHolidayOverride("", monthYear);
            }}
            variant="secondary"
            className="px-4 py-2 rounded-lg shadow-sm transition-colors duration-200 flex items-center gap-2"
            size="sm"
          >
            <AlertTriangle className="h-4 w-4" />
            Holiday Overrides
          </Button>
        </div>
        
        {/* Tab Content */}
        <div className="mt-6">
          <TabsContent value="groups" className="space-y-6">
            {/* Group Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/50 dark:to-blue-900/50 p-4 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Groups</p>
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{totalGroups}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/50 dark:to-purple-900/50 p-4 rounded-lg border border-purple-200/50 dark:border-purple-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    <Building className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-purple-700 dark:text-purple-300">Residence</p>
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{residenceGroups}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/50 dark:to-amber-900/50 p-4 rounded-lg border border-amber-200/50 dark:border-amber-800/50">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                    <UserX className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Non-Residence</p>
                    <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{nonResidenceGroups}</p>
                  </div>
                </div>
              </div>
            </div>
            
            <DutyRosterGroups companyId={companyId} />
          </TabsContent>

          <TabsContent value="calendar" className="space-y-6">
            <RosterCalendar 
              companyId={companyId}
              onOpenHolidayOverride={handleOpenHolidayOverride}
            />
          </TabsContent>
        </div>
      </Tabs>

      {/* Holiday Override Modal */}
      <HolidayOverrideModal
        groupId={selectedGroupForOverride}
        monthYear={selectedMonthForOverride}
        isOpen={holidayOverrideModalOpen}
        onClose={handleCloseHolidayOverride}
      />
    </div>
  );
}
